import React from 'react';
import { StandardSection, createEntitySectionConfig } from '../common';
import type { TableColumn } from '../common';
import type { Equipo, Operadora } from '../../types';
import { equipoFormFields } from '../../config/formFields';

interface EquiposSectionProps {
  data: {
    equipos: Equipo[];
    operadoras: Operadora[];
  };
  operations: {
    handleEquipoSubmit: (data: Record<string, any>) => Promise<void>;
    handleEquipoToggle: (item: Equipo) => Promise<void>;
    handleEquipoDelete: (item: Equipo) => Promise<void>;
    handleEquipoEdit: (item: Equipo, key: string, newValue: any) => Promise<void>;
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

export const EquiposSection: React.FC<EquiposSectionProps> = ({ data, operations, userPermissions }) => {
  // Update form fields with dynamic operadora options
  const equipoFormFieldsWithOperadoras = equipoFormFields.map(field =>
    field.key === 'operadoraId'
      ? { ...field, options: data.operadoras.map(op => ({ value: op.id, label: op.nombre })) }
      : field
  );

  // Table column configurations
  const equipoColumns: TableColumn<Equipo>[] = [
    {
      key: 'nombre',
      title: 'Nombre del Equipo',
      width: '35%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleEquipoEdit
    },
    {
      key: 'operadoraId',
      title: 'Operadora',
      width: '30%',
      editable: true,
      editType: 'select',
      selectOptions: data.operadoras.map(op => ({ value: op.id, label: op.nombre })),
      onEdit: operations.handleEquipoEdit,
      render: (value) => {
        const operadora = data.operadoras.find(op => op.id === value);
        return operadora?.nombre || '(Sin asignar)';
      }
    }
  ];

  // Quick Setup Handler for standard equipment
  const handleEquiposQuickSetup = async () => {
    if (data.operadoras.length === 0) {
      alert('⚠️ Primero debes crear operadoras antes de cargar equipos.');
      return;
    }

    if (window.confirm('¿Cargar equipos estándar? Esta acción creará equipos para cada operadora registrada.')) {
      const equipmentTypes = [
        'Equipo 101', 'Equipo 201', 'Equipo 301',
        'Rig A', 'Rig B', 'Rig C',
        'Torre 1', 'Torre 2'
      ];

      try {
        for (const operadora of data.operadoras.slice(0, 3)) { // Solo las primeras 3 operadoras
          for (let i = 0; i < 2; i++) { // 2 equipos por operadora
            await operations.handleEquipoSubmit({
              nombre: `${equipmentTypes[i]} - ${operadora.nombre.split(' ')[0]}`,
              operadoraId: operadora.id,
              habilitado: true
            });
          }
        }
        alert('✅ Equipos estándar cargados exitosamente');
      } catch (error) {
        console.error('Error cargando equipos:', error);
        alert('❌ Error cargando equipos. Ver consola para detalles.');
      }
    }
  };

  return (
    <>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>🔧 Gestión de Equipos</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Administra los equipos de perforación y sus asignaciones a operadoras
        </p>
      </div>

      {/* Equipos Section */}
      <StandardSection
        config={createEntitySectionConfig(
          'Equipos de Perforación',
          '🔧',
          data.equipos,
          equipoFormFieldsWithOperadoras,
          equipoColumns,
          operations.handleEquipoSubmit,
          operations.handleEquipoToggle,
          operations.handleEquipoDelete,
          {
            description: 'Gestiona los equipos de perforación asignados a cada operadora para operaciones de control de sólidos',
            quickSetup: {
              title: 'Carga Rápida de Equipos',
              description: 'Crea equipos estándar asignados a las operadoras registradas (Rig A, Rig B, Torre 1, etc.)',
              buttonLabel: '🔧 Cargar Equipos Estándar',
              onQuickSetup: handleEquiposQuickSetup
            }
          }
        )}
      />

      {/* Summary Section */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#e3f2fd',
        borderRadius: '12px',
        border: '2px solid #2196F3'
      }}>
        <h3 style={{ color: '#1565C0', marginBottom: '15px' }}>📊 Resumen de Equipos</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginTop: '15px'
        }}>
          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🔧 Total Equipos
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#2196F3'
            }}>
              {data.equipos.length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              ✅ Activos
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#4CAF50'
            }}>
              {data.equipos.filter(eq => eq.habilitado).length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🏢 Operadoras
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#FF9800'
            }}>
              {new Set(data.equipos.map(eq => eq.operadoraId)).size}
            </div>
          </div>
        </div>

        {/* Equipos por Operadora */}
        {data.operadoras.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ color: '#1565C0', marginBottom: '10px' }}>Equipos por Operadora:</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px' }}>
              {data.operadoras.map(operadora => {
                const equiposCount = data.equipos.filter(eq => eq.operadoraId === operadora.id).length;
                return (
                  <div key={operadora.id} style={{
                    padding: '10px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '14px'
                  }}>
                    <span>{operadora.nombre}</span>
                    <span style={{
                      fontWeight: 'bold',
                      color: equiposCount > 0 ? '#4CAF50' : '#999'
                    }}>
                      {equiposCount} equipos
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {data.equipos.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            No hay equipos registrados. Primero crea operadoras, luego usa la carga rápida.
          </div>
        )}
      </div>
    </>
  );
};